/**
 * Monte Carlo — the viability question asked a thousand times.
 *
 * The engine answers "does this scope fit?" once, against one set of estimates.
 * This asks it repeatedly against sampled ones and reports how often the answer
 * came back yes, which is the form a commitment actually needs: not "34 work
 * units fit" but "34 work units fit in half of futures, 26 in four out of five".
 *
 * The arithmetic lives in `src/lib/planningMonteCarlo.ts`; this file runs it in
 * slices so the tab stays alive, and presents it.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Dices, Loader2, RotateCcw, Sliders, XCircle } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import { usePlanningMonteCarloStore } from '../../../stores/prioritization/planningMonteCarloStore';
import {
  FIRM_PROBABILITY,
  MONTE_CARLO_BAND_LABELS,
  OUT_PROBABILITY,
  histogram,
  type MonteCarloBand,
  type MonteCarloConfig,
  type MonteCarloDistribution,
  type MonteCarloPriorityRecord,
  type MonteCarloResourceRecord,
} from '../../../lib/planningMonteCarlo';
import { engineInstanceKey } from '../../../lib/planningEngineService';
import { DataTable, Field, Panel, Select, Switch, toneColor } from '../../../components/ds';
import type { Column, Tone } from '../../../components/ds';

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

const pct = (value: number) => `${Math.round(value * 100)}%`;
const wu = (value: number) => value.toFixed(1);

function bandClass(band: MonteCarloBand): string {
  switch (band) {
    case 'firm':
      return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] dark:bg-[var(--green)]/20';
    case 'at_risk':
      return 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20';
    case 'out':
      return 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20';
  }
}

function bandBarClass(band: MonteCarloBand): string {
  switch (band) {
    case 'firm':
      return 'bg-[var(--green)]';
    case 'at_risk':
      return 'bg-[var(--mustard)]';
    case 'out':
      return 'bg-[var(--coral)]';
  }
}

/** A percentage as a filled bar — the column people actually read. */
function ProbabilityBar({ value, band }: { value: number; band: MonteCarloBand }) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-2 w-24 bg-[var(--rule)] shrink-0">
        <div
          className={`absolute inset-y-0 left-0 ${bandBarClass(band)}`}
          style={{ width: `${Math.max(value * 100, value > 0 ? 2 : 0)}%` }}
        />
      </div>
      <span className="tabular-nums text-[var(--text-pri)] w-10 text-right">{pct(value)}</span>
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone = 'text-[var(--text-pri)]',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: string;
}) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
      <div className="text-[11px] uppercase tracking-wide text-[var(--text-ter)]">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-1 ${tone}`}>{value}</div>
      {hint && <div className="text-xs text-[var(--text-ter)] mt-1">{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

const BAND_TONE: Record<MonteCarloBand, Tone> = {
  firm: 'green',
  at_risk: 'mustard',
  out: 'coral',
};

const PRIORITY_COLUMNS: Column<MonteCarloPriorityRecord>[] = [
  {
    key: 'rank',
    header: '#',
    width: '3rem',
    render: (r) => (
      <span className="tabular-nums text-[var(--text-ter)]">{r.forceRank ?? '\u2014'}</span>
    ),
  },
  {
    key: 'name',
    header: 'Priority',
    render: (r) => <span className="text-[var(--text-pri)]">{r.name}</span>,
  },
  {
    key: 'carries',
    header: 'Carries',
    width: '11rem',
    render: (r) => <ProbabilityBar value={r.inclusionProbability} band={r.band} />,
  },
  {
    key: 'fully',
    header: 'Fully',
    numeric: true,
    width: '5rem',
    render: (r) => pct(r.fullInclusionProbability),
  },
  {
    key: 'coverage',
    header: 'Coverage P10\u2013P90',
    numeric: true,
    width: '9rem',
    render: (r) => `${Math.round(r.coverageP10)}\u2013${Math.round(r.coverageP90)}%`,
  },
  {
    key: 'size',
    header: 'Size (WU)',
    numeric: true,
    width: '6rem',
    render: (r) => wu(r.meanRequiredWU),
  },
  {
    key: 'band',
    header: 'Band',
    width: '6rem',
    render: (r) => (
      <span className={`px-2 py-0.5 text-xs ${bandClass(r.band)}`}>
        {MONTE_CARLO_BAND_LABELS[r.band]}
      </span>
    ),
  },
  {
    key: 'reason',
    header: 'Most often stopped by',
    cellClassName: 'text-xs text-[var(--text-ter)]',
    render: (r) =>
      r.topExclusionReason ? (
        <>
          <span className="tabular-nums">{pct(r.topExclusionReasonShare)}</span>{' '}
          {r.topExclusionReason}
        </>
      ) : (
        '\u2014'
      ),
  },
];

const RESOURCE_COLUMNS: Column<MonteCarloResourceRecord>[] = [
  {
    key: 'resourceType',
    header: 'Resource type',
    render: (r) => <span className="text-[var(--text-pri)] capitalize">{r.resourceType}</span>,
  },
  {
    key: 'p50',
    header: 'Utilization P50',
    numeric: true,
    width: '9rem',
    render: (r) => `${Math.round(r.utilizationP50)}%`,
  },
  {
    key: 'p90',
    header: 'Utilization P90',
    numeric: true,
    width: '9rem',
    // Over 100% is the whole point of the column — it has to look different.
    render: (r) => (
      <span className={r.utilizationP90 > 100 ? 'text-[var(--coral)]' : undefined}>
        {Math.round(r.utilizationP90)}%
      </span>
    ),
  },
  {
    key: 'over',
    header: 'Over-subscribed',
    numeric: true,
    width: '11rem',
    render: (r) => `${pct(r.overSubscribedProbability)} of futures`,
  },
];

// ---------------------------------------------------------------------------
// Outcome histogram
// ---------------------------------------------------------------------------

function OutcomeChart({ distribution }: { distribution: MonteCarloDistribution }) {
  const bins = useMemo(() => histogram(distribution, 24), [distribution]);
  const data = useMemo(
    () =>
      bins.map((bin) => ({
        // Bin midpoint: the bar stands for a range, and the axis has to say so
        // in one number.
        x: (bin.start + bin.end) / 2,
        count: bin.count,
        label: `${wu(bin.start)}–${wu(bin.end)} WU`,
      })),
    [bins],
  );

  const slate = toneColor('slate');
  const mustard = toneColor('mustard');
  const green = toneColor('green');
  const rule = 'var(--rule)';

  if (data.length === 0) return null;

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={rule} vertical={false} />
          <XAxis
            dataKey="x"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v: number) => wu(v)}
            tick={{ fontSize: 11, fill: 'var(--text-ter)' }}
            stroke={rule}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--text-ter)' }}
            stroke={rule}
            label={{
              value: 'futures',
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 11, fill: 'var(--text-ter)' },
            }}
          />
          <Tooltip
            cursor={{ fill: 'var(--rule)', opacity: 0.4 }}
            contentStyle={{
              background: 'var(--bg-raised)',
              border: '1px solid var(--rule-bright)',
              fontSize: 12,
            }}
            labelFormatter={(_v, payload) => payload?.[0]?.payload?.label ?? ''}
            formatter={(value) => [`${value} futures`, '']}
          />
          {/* The two numbers a commitment is actually made against. */}
          <ReferenceLine
            x={distribution.p50}
            stroke={mustard}
            strokeDasharray="3 3"
            label={{ value: 'P50', position: 'top', style: { fontSize: 11, fill: mustard } }}
          />
          <ReferenceLine
            x={distribution.p80}
            stroke={green}
            strokeDasharray="3 3"
            label={{ value: 'P80', position: 'top', style: { fontSize: 11, fill: green } }}
          />
          <Bar dataKey="count" isAnimationActive={false}>
            {data.map((bin) => (
              <Cell
                key={bin.x}
                // Left of P80 is the scope that held in four futures out of
                // five; right of it is scope you only get in a good one.
                fill={bin.x <= distribution.p80 ? green : slate}
                fillOpacity={bin.x <= distribution.p80 ? 0.85 : 0.5}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

const ITERATION_OPTIONS = [
  { value: '200', label: '200 — quick look' },
  { value: '500', label: '500 — default' },
  { value: '1000', label: '1,000' },
  { value: '2000', label: '2,000 — slow, tighter tails' },
];

const SPREAD_OPTIONS = [
  { value: '0.1', label: 'Tight (±10%)' },
  { value: '0.2', label: 'Typical (±20%)' },
  { value: '0.35', label: 'Loose (±35%)' },
  { value: '0.5', label: 'Very loose (±50%)' },
];

const BIAS_OPTIONS = [
  { value: '1', label: 'Unbiased — estimates land on average' },
  { value: '1.1', label: 'Runs 10% over' },
  { value: '1.25', label: 'Runs 25% over' },
  { value: '1.5', label: 'Runs 50% over' },
];

const CORRELATION_OPTIONS = [
  { value: '0', label: 'None — every item independent' },
  { value: '0.35', label: 'Moderate — default' },
  { value: '0.7', label: 'Strong' },
  { value: '0.9', label: 'Near lockstep' },
];

const CAPACITY_OPTIONS = [
  { value: '0', label: 'Pools are exactly as planned' },
  { value: '0.1', label: 'Typical (±10%)' },
  { value: '0.2', label: 'Volatile (±20%)' },
];

function SettingsPanel({
  config,
  disabled,
  onChange,
  onReset,
}: {
  config: MonteCarloConfig;
  disabled: boolean;
  onChange: (patch: Partial<MonteCarloConfig>) => void;
  onReset: () => void;
}) {
  return (
    <Panel
      label="Assumptions"
      title="What the simulation varies"
      actions={
        <button
          type="button"
          onClick={onReset}
          disabled={disabled}
          className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)] disabled:opacity-50"
        >
          <RotateCcw className="h-3 w-3" />
          Reset
        </button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Field label="Futures sampled" hint="More trials, steadier tails, slower run.">
          <Select
            size="sm"
            disabled={disabled}
            value={String(config.iterations)}
            options={ITERATION_OPTIONS}
            onChange={(e) => onChange({ iterations: Number(e.target.value) })}
          />
        </Field>

        <Field label="Estimate spread" hint="How far a typical estimate can miss.">
          <Select
            size="sm"
            disabled={disabled}
            value={String(config.baseSpread)}
            options={SPREAD_OPTIONS}
            onChange={(e) => onChange({ baseSpread: Number(e.target.value) })}
          />
        </Field>

        <Field
          label="Estimate bias"
          hint="Systematic optimism, if this portfolio has a track record of it."
        >
          <Select
            size="sm"
            disabled={disabled}
            value={String(config.demandBias)}
            options={BIAS_OPTIONS}
            onChange={(e) => onChange({ demandBias: Number(e.target.value) })}
          />
        </Field>

        <Field
          label="Correlation"
          hint="Independent draws cancel out and overstate confidence."
        >
          <Select
            size="sm"
            disabled={disabled}
            value={String(config.correlation)}
            options={CORRELATION_OPTIONS}
            onChange={(e) => onChange({ correlation: Number(e.target.value) })}
          />
        </Field>

        <Field label="Capacity variability" hint="Pool availability is a forecast too.">
          <Select
            size="sm"
            disabled={disabled}
            value={String(config.capacitySpread)}
            options={CAPACITY_OPTIONS}
            onChange={(e) => onChange({ capacitySpread: Number(e.target.value) })}
          />
        </Field>

        <Field
          label="Seed"
          hint="The same seed always reproduces the same run."
        >
          <Select
            size="sm"
            disabled={disabled}
            value={String(config.seed)}
            options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `Seed ${n}` }))}
            onChange={(e) => onChange({ seed: Number(e.target.value) })}
          />
        </Field>
      </div>

      <div className="mt-4 pt-4 border-t border-[var(--rule)] flex flex-col gap-3">
        <Switch
          checked={config.signalWeight > 0}
          disabled={disabled}
          onChange={(next) => onChange({ signalWeight: next ? 1 : 0 })}
          label="Widen spread per item from the signals already recorded"
          description="Undecomposed work, assumption flags, risk level and confidence each widen that item's range. Off simulates one uniform spread across the portfolio."
        />
        <Switch
          checked={config.participationRisk}
          disabled={disabled}
          onChange={(next) => onChange({ participationRisk: next })}
          label="Resolve unconfirmed participation per future"
          description="A tentative or unanswered cross-team request lands or does not, trial by trial, instead of being taken at its current answer."
        />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export default function MonteCarloView() {
  const {
    projectId,
    activeScenario,
    engineInput,
    scopedItemIds,
    planOutput,
    activeInstanceLabel,
  } = usePlanningEngine();

  const { config, result, running, progress, error, setConfig, resetConfig, clearIfStale, run, cancel } =
    usePlanningMonteCarloStore();

  const [showSettings, setShowSettings] = useState(false);

  const instanceKey = activeScenario ? engineInstanceKey(activeScenario.id, engineInput) : null;

  // A probability computed against a different scenario or scope is not a
  // weaker answer to this question — it is an answer to a different one.
  useEffect(() => {
    clearIfStale(instanceKey);
  }, [instanceKey, clearIfStale]);

  const startRun = () => {
    if (!projectId || !activeScenario) return;
    void run({ projectId, scenario: activeScenario, engineInput, scopedItemIds });
  };

  const canRun = Boolean(projectId && activeScenario) && !running;

  // The engine's own single-pass answer, as the point of comparison.
  const deterministicCarried = planOutput
    ? planOutput.prioritySummaries.filter(
        (s) => s.feasibility === 'Fully Achievable' || s.feasibility === 'Partially Achievable',
      ).length
    : null;

  return (
    <div className="p-4 md:p-6 w-full h-full overflow-y-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20">
              <Dices className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)]">Monte Carlo</h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] ml-10 max-w-3xl">
            {result
              ? `${result.iterations.toLocaleString()} futures simulated against ${activeInstanceLabel} · ${(result.elapsedMs / 1000).toFixed(1)}s · seed ${result.config.seed}`
              : 'Runs the planning engine repeatedly against sampled estimates, capacity and participation, and reports how often each priority carried.'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowSettings((v) => !v)}
            className="flex items-center gap-2 px-3 py-2 border border-[var(--rule)] text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            <Sliders className="h-4 w-4" />
            Assumptions
          </button>
          {running ? (
            <button
              type="button"
              onClick={cancel}
              className="flex items-center gap-2 px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)]"
            >
              <XCircle className="h-4 w-4" />
              Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={startRun}
              disabled={!canRun}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium"
            >
              <Dices className="h-4 w-4" />
              {result ? 'Re-run simulation' : 'Run simulation'}
            </button>
          )}
        </div>
      </div>

      {!activeScenario && (
        <div className="bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] px-3 py-2 text-sm text-[var(--mustard)]">
          No scenario selected. Pick one above — the simulation runs against the active
          Scenario × Input Scope, the same instance the engine runs.
        </div>
      )}

      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] p-3 text-sm text-[var(--coral)]">
          {error}
        </div>
      )}

      {showSettings && (
        <SettingsPanel
          config={config}
          disabled={running}
          onChange={setConfig}
          onReset={resetConfig}
        />
      )}

      {running && (
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
          <div className="flex items-center gap-2 text-sm text-[var(--text-sec)] mb-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Simulating {Math.round(progress * config.iterations).toLocaleString()} of{' '}
            {config.iterations.toLocaleString()} futures…
          </div>
          <div className="h-1.5 w-full bg-[var(--rule)]">
            <div
              className="h-full bg-[var(--slate)] transition-[width] duration-150"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      )}

      {!result && !running && activeScenario && (
        <div className="border border-dashed border-[var(--rule)] p-8 text-center">
          <Dices className="h-6 w-6 mx-auto text-[var(--text-ter)] mb-3" />
          <p className="text-sm text-[var(--text-sec)] max-w-xl mx-auto">
            The engine answers the viability question once, against one set of estimates.
            This asks it {config.iterations.toLocaleString()} times against sampled ones, so
            the answer comes back as a probability per priority and a range for the scope —
            not a single number that reads more certain than it is.
          </p>
        </div>
      )}

      {result && (
        <>
          {/* Headline */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              label="P50 scope"
              value={`${wu(result.carriedWorkUnits.p50)} WU`}
              hint="Carried in half of futures"
            />
            <StatTile
              label="P80 scope"
              value={`${wu(result.carriedWorkUnits.p80)} WU`}
              hint="Carried in four futures out of five"
              tone="text-[var(--green)] dark:text-[var(--text-ter)]"
            />
            <StatTile
              label="Firm priorities"
              value={String(result.firmCount)}
              hint={`Carry at least ${pct(FIRM_PROBABILITY)} of the time`}
              tone="text-[var(--green)] dark:text-[var(--text-ter)]"
            />
            <StatTile
              label="At risk"
              value={String(result.atRiskCount)}
              hint={`${result.outCount} never carried above ${pct(OUT_PROBABILITY)}`}
              tone="text-[var(--mustard)]"
            />
          </div>

          <Panel
            label="Commitment"
            title="What this says to commit to"
            accent={result.carriedWorkUnits.p80 > 0 ? 'green' : 'coral'}
          >
            <p className="text-sm text-[var(--text-sec)] leading-relaxed">
              Committing to{' '}
              <span className="font-semibold text-[var(--text-pri)] tabular-nums">
                {wu(result.carriedWorkUnits.p80)} work units
              </span>{' '}
              holds in 80% of the {result.iterations.toLocaleString()} futures simulated.
              Committing to the median{' '}
              <span className="tabular-nums">{wu(result.carriedWorkUnits.p50)}</span> is a
              coin flip, and the plan carried between{' '}
              <span className="tabular-nums">{wu(result.carriedWorkUnits.min)}</span> and{' '}
              <span className="tabular-nums">{wu(result.carriedWorkUnits.max)}</span> across
              the full range.
              {deterministicCarried != null && (
                <>
                  {' '}The engine's single pass carried{' '}
                  <span className="tabular-nums">{deterministicCarried}</span>{' '}
                  {deterministicCarried === 1 ? 'priority' : 'priorities'}; the simulation
                  carried a median of{' '}
                  <span className="tabular-nums">{result.carriedCount.p50}</span>.
                </>
              )}
            </p>
          </Panel>

          {/* Distribution */}
          <Panel label="Distribution" title="Scope carried, across every future">
            <OutcomeChart distribution={result.carriedWorkUnits} />
          </Panel>

          {/* Priorities */}
          <Panel
            label="Per priority"
            title="How often each one carried"
            count={result.priorityRecords.length}
            flush
          >
            <DataTable
              rows={result.priorityRecords}
              getRowKey={(r) => r.priorityId}
              getRowAccent={(r) => BAND_TONE[r.band]}
              ariaLabel="Carry probability per priority"
              columns={PRIORITY_COLUMNS}
            />
          </Panel>

          {/* Pools */}
          {result.resourceRecords.length > 0 && (
            <Panel
              label="Pools"
              title="Where the pressure lands"
              count={result.resourceRecords.length}
              flush
            >
              <DataTable
                rows={result.resourceRecords}
                getRowKey={(r) => r.resourceType}
                ariaLabel="Pool pressure across futures"
                columns={RESOURCE_COLUMNS}
              />
            </Panel>
          )}

          <p className="text-xs text-[var(--text-ter)] max-w-3xl">
            Spreads are derived from what the model already records — decomposition maturity,
            assumption flags, risk level, confidence, participation status — not from
            estimate-versus-actual history, which this workspace does not have yet. Treat the
            probabilities as a calibrated way to compare items and scopes against each other,
            not as a forecast validated against outcomes.
          </p>
        </>
      )}
    </div>
  );
}
